<template>
  <div class="bar-result-faq">
    <div class="search">
      <div class="title">
        <span class="material-symbols-outlined"> help </span>
        <p class="body-6-bold">{{ $t('faq.titlehelp') }}</p>
      </div>
      <searchcomponent :placeholder="$t('faq.search')" />
    </div>
    <div
      v-for="(item, index) in currentLanguageLinks"
      :key="item.link"
      class="accordeon var-two"
      :class="{ active: activeAccordion === index }"
    >
      <div v-if="item.links && item.links.length" class="accordeon-box">
        <div :class="['box', { activeOption: activeAccordion === index }]">
          <div class="box-content" @click="toggleAccordion(index)"> 
            <div class="box-text">
              <div class="title-icon">
                <div class="box-icon">
                  <div class="icon">
                    <span class="material-symbols-outlined" translate="no">{{ item.icon }}</span>
                  </div>
                </div>
                <p class="title body-6">{{ $t(item.title) }}</p>
              </div>
              <div v-if="activeAccordion === index" class="accordion-content">
                <div class="tabs">
                  <div class="tabs-container">
                    <div class="tabs-buttons">
                      <div v-for="(link, linkIndex) in item.links" :key="link.url">
                        <NuxtLink
                          :to="localPath(`/faq/${link.url}`)"
                          class="tab-button"
                          @click.native.prevent="
                            handleLinkClick(link, index, linkIndex)
                          "
                        >
                          {{ $t(link.text) }}
                        </NuxtLink>
                      </div>
                    </div>
                    <div class="select-custom selectItem">
                      <label></label>
                      <div
                        class="select-content"
                        :class="{ active: showSelectOptions[`${index}`] }"
                      >
                        <div
                          class="select-toggle"
                          @click.stop="toggleSelect(`${index}`)"
                        >
                          {{
                            activeTab[`${index}`]
                              ? $t(activeTab[`${index}`].text)
                              : $t('global.select-option')
                          }}
                          <span class="material-symbols-outlined" translate="no">
                            {{ showSelectOptions[`${index}`] ? 'expand_less' : 'expand_more' }}
                          </span>
                        </div>
                        <div class="select-options" v-if="showSelectOptions[`${index}`]">
                          <NuxtLink
                            v-for="(link, linkIndex) in item.links"
                            :key="link.url"
                            :to="localPath(`/faq/${link.url}`)"
                            class="select-option"
                            :class="{
                              activeTab:
                                activeTab[`${index}`]?.url === link.url ||
                                (initialActiveTab[index] === linkIndex &&
                                  !activeTab[`${index}`]),
                            }"
                            @click.native.prevent="
                              handleLinkClick(link, index, linkIndex);
                              closeSelect(`${index}`);
                            "
                          >
                            {{ $t(link.text) }}
                          </NuxtLink>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="arrow">
              <span class="material-symbols-outlined" translate="no">
                {{ activeAccordion === index ? 'expand_less' : 'expand_more' }}
              </span>
            </div>
          </div>
          <div
            v-if="activeAccordion === index"
            class="mobile-content"
          >
            <slot />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick, watch, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useLocalePath, useRoute, useRouter } from '#imports';
import faqs from '~/assets/faqs.json';
import searchcomponent from '~/components/search-component.vue';

const { t: $t } = useI18n();
const localPath = useLocalePath();
const route = useRoute();
const router = useRouter();

const activeAccordion = ref(null);
const showSelectOptions = ref({});
const activeTab = ref({});
const initialActiveTab = ref({});
const isMobile = ref(false);

function toggleAccordion(index) {
  activeAccordion.value = activeAccordion.value === index ? null : index;
  checkIsMobile();

  nextTick(() => {
    window.scrollTo({ top: 10, behavior: 'smooth' });
  });

  if (activeAccordion.value === index && isMobile.value) {
    nextTick(() => {
      const firstLink = currentLanguageLinks.value[index].links[0];
      handleLinkClick(firstLink, index, 0);
    });
  }
}

function toggleSelect(key) {
  showSelectOptions.value[key] = !showSelectOptions.value[key];
}

function setActiveTab(tab, key) {
  activeTab.value[key] = tab;
}

function closeSelect(key) {
  showSelectOptions.value[key] = false;
}

function handleLinkClick(link, index, linkIndex) {
  setActiveTab(link, `${index}`);
  initialActiveTab.value[index] = linkIndex; 
  router.push(localPath(`/faq/${link.url}`)).then(() => {
    window.scrollTo({ top: 10, behavior: 'smooth' }); 
  });
}

onMounted(() => {
  const currentPath = route.path;
  const initialIndex = faqs.findIndex((item) =>
    item.links.some((link) => currentPath.includes(link.url))
  );
  activeAccordion.value = initialIndex !== -1 ? initialIndex : null;

  if (activeAccordion.value !== null) {
    const activeLinkIndex = faqs[activeAccordion.value].links.findIndex(
      (link) => currentPath.includes(link.url)
    );
    if (activeLinkIndex !== -1) {
      const activeLink = faqs[activeAccordion.value].links[activeLinkIndex];
      setActiveTab(activeLink, `${activeAccordion.value}`);
      initialActiveTab.value[activeAccordion.value] = activeLinkIndex;
    }
  }

  checkIsMobile();

  window.addEventListener('resize', checkIsMobile);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkIsMobile);
});

function checkIsMobile() {
  isMobile.value = window.innerWidth <= 768;
}

const currentLanguageLinks = computed(() => {
  return faqs.map((item) => ({
    title: item.title,
    icon: item.icon,
    links: item.links.map((link) => ({
      text: link.text,
      url: link.url,
    })),
  }));
});
</script>